/**
 * Collaboration utilities — Tiptap v3 + Yjs + y-websocket
 *
 * Uses:
 *  - @tiptap/extension-collaboration  (bundles @tiptap/y-tiptap which wraps y-prosemirror)
 *  - y-websocket WebsocketProvider for real-time sync
 *  - yCursorPlugin from @tiptap/y-tiptap for live cursors
 */
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { yCursorPlugin, defaultCursorBuilder } from '@tiptap/y-tiptap';
import { Collaboration } from '@tiptap/extension-collaboration';
import { Extension } from '@tiptap/core';
import { getAuthToken } from './api';

// ── Config ────────────────────────────────────────────────────────────────────

const RAW_WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3001/api';

/** Convert http(s) API base-URL to ws(s). */
function toWsUrl(apiUrl: string): string {
  return apiUrl.replace(/^http/, 'ws');
}

// y-websocket server URL format: serverUrl/roomname
// Our route is /api/ws/:chapterId  →  serverUrl = ws://host:3001/api/ws, roomname = chapterId
const WS_BASE = toWsUrl(RAW_WS_URL) + '/ws';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CollaboratorUser {
  clientId: number;
  id?: string;
  name: string;
  color: string;
  avatarUrl?: string | null;
}

interface CollaborationState {
  ydoc: Y.Doc;
  provider: WebsocketProvider;
  connected: boolean;
  users: CollaboratorUser[];
  /** How many mounted consumers hold this chapter's instance (see refcount below). */
  refCount: number;
  /** Pending teardown timer — lets a remount reclaim the instance before it's torn down. */
  destroyTimer: ReturnType<typeof setTimeout> | null;
}

// ── Per-chapter instances ─────────────────────────────────────────────────────

const instances = new Map<string, CollaborationState>();

// ── Color derivation ──────────────────────────────────────────────────────────

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
];

/** Deterministic color from a user-id string (no Math.random so it's stable). */
export function colorFromUserId(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash |= 0;
  }
  return COLORS[Math.abs(hash) % COLORS.length];
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Create (or return existing) collaboration state for a chapter.
 * Call this BEFORE building the Tiptap editor.
 */
export function initializeCollaboration(
  chapterId: string,
  userId: string,
  userName: string,
  onUsersChange?: (users: CollaboratorUser[]) => void,
): CollaborationState {
  // Reference-count consumers. During a page transition React briefly mounts TWO
  // EditorViews for the same chapter (the exiting one + the entering one). Both
  // share this single instance; without refcounting the exiting one's unmount
  // destroyed the provider the active one was using → the socket died and live
  // sync silently stopped ("intermittent" collab). Refcount + a short teardown
  // delay keep the instance alive as long as anyone is using it.
  const existing = instances.get(chapterId);
  if (existing) {
    existing.refCount += 1;
    if (existing.destroyTimer) { clearTimeout(existing.destroyTimer); existing.destroyTimer = null; }
    return existing;
  }

  const ydoc = new Y.Doc();
  const color = colorFromUserId(userId);

  // Native (Capacitor) can't send the session cookie, so pass the Bearer session
  // id as ?token= — the WS server reads cookie OR token. On web, token is null
  // and the same-origin cookie authenticates.
  const token = getAuthToken();
  const provider = new WebsocketProvider(WS_BASE, chapterId, ydoc, {
    connect: true,
    // y-websocket passes params as query string: WS_BASE/chapterId?chapterId=...
    params: token ? { chapterId, token } : { chapterId },
    // Mobile suspends the socket when the keyboard opens or the app backgrounds;
    // y-websocket reconnects but can miss updates in the gap. Periodically resync
    // the whole doc so both devices converge even after a flaky mobile connection
    // — this is what fixes the "intermittent" real-time sync on phones.
    resyncInterval: 4000,
  });

  // Set local user awareness state
  provider.awareness.setLocalStateField('user', {
    id: userId,
    name: userName,
    color,
  });

  const state: CollaborationState = {
    ydoc,
    provider,
    connected: false,
    users: [],
    refCount: 1,
    destroyTimer: null,
  };

  // Track connection status
  provider.on('status', (event: { status: string }) => {
    state.connected = event.status === 'connected';
    console.log(`[Collab:${chapterId}] status →`, event.status);
  });

  // Track remote users via awareness — but only ACTUAL other people. The same
  // account open on a second device (same userId, different clientID) is still
  // "you", so it must not show up as a collaborator or trigger the "someone
  // joined" ceremony (the two-candles animation).
  provider.awareness.on('change', () => {
    const users: CollaboratorUser[] = [];
    provider.awareness.getStates().forEach((awarenessState, clientId) => {
      const remoteUser = awarenessState.user as { id?: string; name?: string; color?: string } | undefined;
      if (
        clientId !== provider.awareness.clientID &&
        remoteUser &&
        remoteUser.id !== userId // skip your own other device(s)
      ) {
        users.push({
          clientId,
          id: remoteUser.id,
          name: remoteUser.name || 'Anonymous',
          color: remoteUser.color || '#888888',
        });
      }
    });
    state.users = users;
    onUsersChange?.(users);
  });

  provider.on('sync', (isSynced: boolean) => {
    console.log(`[Collab:${chapterId}] synced →`, isSynced);
  });

  instances.set(chapterId, state);
  return state;
}

/**
 * Destroy collaboration instance for a chapter (call on unmount / chapter change).
 */
export function destroyCollaboration(chapterId: string): void {
  const state = instances.get(chapterId);
  if (!state) return;
  state.refCount -= 1;
  if (state.refCount > 0) return; // another mounted consumer still needs it
  // Last consumer left — but defer the teardown briefly so a same-tick remount
  // (page transition, StrictMode) can reclaim the live socket instead of forcing
  // a full reconnect. If nobody reclaims it within the delay, tear it down.
  if (state.destroyTimer) return;
  state.destroyTimer = setTimeout(() => {
    const s = instances.get(chapterId);
    if (!s || s.refCount > 0) return; // reclaimed in the meantime
    s.provider.disconnect();
    s.provider.destroy();
    s.ydoc.destroy();
    instances.delete(chapterId);
    console.log(`[Collab:${chapterId}] destroyed`);
  }, 1500);
}

/**
 * Get the Tiptap extensions for collaborative editing.
 * Must be called after initializeCollaboration.
 */
export function getCollaborationExtensions(chapterId: string): Extension[] {
  const state = instances.get(chapterId);
  if (!state) {
    console.warn(`[Collab] No instance for chapter ${chapterId}`);
    return [];
  }

  const fragment = state.ydoc.getXmlFragment('default');

  const extensions: Extension[] = [
    // Tiptap v3 Collaboration: binds the editor to the Y.XmlFragment
    Collaboration.configure({
      fragment,
    }) as Extension,
  ];

  // Add cursor plugin via low-level ProseMirror plugin wrapped as Tiptap extension
  if (state.provider.awareness) {
    // Your own other device shares your userId — don't paint it as a remote
    // cursor with your name, that reads as "another person editing".
    const localUserId = (state.provider.awareness.getLocalState()?.user as { id?: string } | undefined)?.id;
    // Live cursors — MUST be a real Extension.create(): a plain object cast to
    // Extension has no .config/.addExtensions, so Tiptap's manager crashed with
    // "Cannot read properties of undefined (reading 'addExtensions')" and the
    // editor went blank — which is why collaboration never actually worked.
    const CursorExtension = Extension.create({
      name: 'yjs-cursor',
      addProseMirrorPlugins() {
        return [
          yCursorPlugin(state.provider.awareness, {
            cursorBuilder: (user: { id?: string; name?: string; color?: string }) => {
              // Suppress the caret for your own other session.
              if (user?.id && localUserId && user.id === localUserId) {
                return document.createElement('span');
              }
              return defaultCursorBuilder(user);
            },
          }),
        ];
      },
    });
    extensions.push(CursorExtension);
  }

  return extensions;
}

/**
 * Seed the Y.Doc with initial HTML content (only if the fragment is empty).
 * Call this after the Tiptap editor is created so we can use its parser.
 */
export function seedDocumentIfEmpty(
  chapterId: string,
  getJsonContent: () => Record<string, unknown>,
): void {
  const state = instances.get(chapterId);
  if (!state) return;

  const fragment = state.ydoc.getXmlFragment('default');
  // If fragment already has content (from server state), don't overwrite
  if (fragment.length > 0) return;

  // Otherwise, the Tiptap editor will have already populated it via its initial content.
  // We just need to make sure we don't double-set; Tiptap's ySyncPlugin does the seeding.
  console.log(`[Collab:${chapterId}] fragment empty, initial content will be seeded by Tiptap`);
}

/**
 * Get the Y.Doc for a chapter (for read-only access).
 */
export function getYDoc(chapterId: string): Y.Doc | null {
  return instances.get(chapterId)?.ydoc ?? null;
}

/**
 * Whether the provider is connected.
 */
export function isConnected(chapterId: string): boolean {
  return instances.get(chapterId)?.connected ?? false;
}

/**
 * Get current remote collaborators.
 */
export function getConnectedUsers(chapterId: string): CollaboratorUser[] {
  return instances.get(chapterId)?.users ?? [];
}

/**
 * Get the WebsocketProvider for a chapter (for external use if needed).
 */
export function getProvider(chapterId: string): WebsocketProvider | null {
  return instances.get(chapterId)?.provider ?? null;
}

